import React from 'react'

// Single-row "Donating $X to Org" summary. v7's Tipping component
// only renders the tipping + cover-costs controls; for PayPal/Venmo
// on partners that disable both (tipping.type == 'none' and
// cover_costs off), the donor would otherwise land on a payment
// page with only the PayPal button and no other context. This
// restores a one-line summary so the page has visible weight.
//
// Only used by PayPal/Venmo: AP/GP show the same info in the wallet
// sheet itself; Stripe gets it in the submit button text via
// donate_total_recurrence; PayPal/Venmo's SDK button doesn't surface
// the amount until the sheet opens, hence the targeted use here.
//
// Layout: preset emoji + matched preset's impact line, line-item
// template split around the beneficiary slot, multi-org pseudo-button.
const DonationLine = ({
  i18n, donation, partnerItem, project, multiOrg, multiProject, multiOrgModal,
}) => {
  const { state, currency, presets } = donation
  const beneficiaryName = partnerItem.beneficiary.name
  // Only the donor's current pick, for the name slot below — the impact
  // gate wants the fundraiser-level `multiOrg` instead, since impact is
  // equally wrong once they've narrowed a split fundraiser to one org.
  const splitSelected = partnerItem.beneficiary.id == '_multiple'
  const [ lineItemBefore, lineItemAfter ] = (i18n.t(
    `subscription_labels.${state.subscription}.lineItemTo`,
  ) || '').split('%{org}')

  // Impact gating matches Amount: suppressed on a fundraiser that splits
  // across orgs or offers a choice of projects. The sub-line stays one
  // line — impact wins over the project name when a sole designation
  // makes both available, since stacking them reads as two unlabeled
  // facts and the project name appears nowhere else in v7 to anchor it.
  const activePreset = !multiOrg && !multiProject
    && (presets || []).find(p => p.amount === state.amount)
  const presetEmoji = activePreset?.emoji
  const extraLine = activePreset?.impact || project?.name

  return (
    <div className="rounded-lg border border-brand-500 bg-brand-50 p-3 @roomy:p-4">
      <div className="flex items-center gap-3">
        <span aria-hidden="true" className="text-xl">
          {presetEmoji || '🎁'}
        </span>
        <div className="flex-1">
          <p className="font-medium">
            {lineItemBefore}
            {splitSelected ? (
              <button
                type="button"
                data-plg-modal-label={i18n.t('selected_nonprofits')}
                data-plg-modal-src={multiOrgModal?.src}
                data-plg-modal-id={multiOrgModal?.id}
                className="btn-link underline underline-offset-2"
              >
                {i18n.t('selected_nonprofits')}
              </button>
            ) : beneficiaryName}
            {lineItemAfter}
          </p>
          {extraLine && (
            <p className="text-sm text-gray-700">{extraLine}</p>
          )}
        </div>
        <div className="text-xl font-semibold">
          {i18n.formatCurrency(state.amount, currency)}
        </div>
      </div>
    </div>
  )
}

export default DonationLine
